import { useTranslation } from 'react-i18next'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../constants/common'
import {
  QA_NAME_PROFILE_INFO_ITEM_TITLE,
  QA_NAME_PROFILE_INFO_ITEM_VALUE,
} from '../../../constants/qa'
import { useContentOverflow, useDateLimits, useTimeZone } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import { toUkTimezoneDate } from '../../../utils/date'
import { stopPropagation } from '../../../utils/events'
import { DateTimePicker } from '../../DateTime'
import { FloatingTooltip } from '../../Floating'
import { Status } from '../../Status'
import Text from '../../Text'
import { EntityProfileHeaderInfoItemBase } from '../EntityProfileHeaderInfoItemBase'

import {
  ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_ACTIONS,
  ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_ACTIONS_KEY_DELETE,
  ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_DATE_QA_NAME,
  ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_QA_NAME,
  ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_TIME_QA_NAME,
} from './EntityProfileHeaderInfoItemDeadline.constants'
import { useEntityProfileHeaderInfoItemDeadlineEdit } from './EntityProfileHeaderInfoItemDeadline.hooks'
import {
  getEntityProfileHeaderInfoItemDeadlineFormattedText,
  getEntityProfileHeaderInfoItemDeadlineTagColorProps,
} from './EntityProfileHeaderInfoItemDeadline.utils'

import * as styles from './EntityProfileHeaderInfoItemDeadline.module.css'

function EntityProfileHeaderInfoItemDeadline({
  EditAddon,
  canEdit,
  className = styles.EntityProfileHeaderInfoItemDeadline,
  editAddonProps,
  editFieldsWrapperClassName,
  noDataTextClassName,
  noDataTextKey,
  ref,
  status,
  title,
  titleClassName,
  tooltipPlacement = FLOATING_ELEMENT_PLACEMENT.top,
  value,
  onSaveValue,
  ...restProps
}) {
  const { t } = useTranslation()

  const timezone = useTimeZone()

  const { maxDate, minDate } = useDateLimits()

  const { overflow, setContentOverflowRef } = useContentOverflow({
    externalRef: ref,
  })

  const {
    dateInputRef,
    editContainerRef,
    editValue,
    handleChangeEditValue,
    handleDeleteValue,
    handleStartEditing,
    hasError,
    isEditing,
    isSaving,
  } = useEntityProfileHeaderInfoItemDeadlineEdit({ value, onSaveValue })

  function handleClickDelete(event) {
    stopPropagation(event)

    handleDeleteValue()
  }

  const ukDeadline = value ? toUkTimezoneDate(value, timezone) : null

  const formattedValue = ukDeadline
    ? getEntityProfileHeaderInfoItemDeadlineFormattedText(ukDeadline)
    : null

  const tagColorProps = ukDeadline
    ? getEntityProfileHeaderInfoItemDeadlineTagColorProps(ukDeadline, status)
    : null

  return isEditing ? (
    <div className={className}>
      <Text
        isSecondary
        className={titleClassName}
        qaName={QA_NAME_PROFILE_INFO_ITEM_TITLE}
      >
        {t(title)}
      </Text>
      <div className={editFieldsWrapperClassName} ref={editContainerRef}>
        <DateTimePicker
          dateProps={{
            autoFocus: true,
            placeholder: t('Request.EditDeadlineFieldDatePlaceholderKey'),
            qaName: ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_DATE_QA_NAME,
            ref: dateInputRef,
            wrapperClassName: styles.datePickerWrap,
          }}
          disabled={isSaving}
          maxValue={maxDate}
          minValue={minDate}
          showError={hasError}
          timeProps={{
            placeholder: t('Request.EditDeadlineFieldTimePlaceholderKey'),
            qaName: ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_TIME_QA_NAME,
            wrapperClassName: styles.timePickerWrap,
          }}
          value={editValue}
          onChange={handleChangeEditValue}
        />
        {EditAddon ? (
          <EditAddon
            {...editAddonProps}
            disabled={isSaving}
            value={editValue}
            onSelectDate={handleChangeEditValue}
          />
        ) : null}
      </div>
    </div>
  ) : (
    <EntityProfileHeaderInfoItemBase
      {...restProps}
      actions={
        canEdit ? ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_ACTIONS : null
      }
      canEdit={canEdit}
      className={className}
      contentExist={!!value}
      contentWrapperProps={
        canEdit
          ? {
              ...getQaSelector({
                value: ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_QA_NAME,
              }),
              onClick: handleStartEditing,
            }
          : null
      }
      handlers={{
        [ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_ACTIONS_KEY_DELETE]: {
          onClick: handleClickDelete,
        },
      }}
      title={title}
      titleClassName={titleClassName}
      value={value}
    >
      {value ? (
        <Status
          {...tagColorProps}
          className={styles.deadline}
          text={formattedValue}
          textQaName={QA_NAME_PROFILE_INFO_ITEM_VALUE}
        />
      ) : (
        <FloatingTooltip
          content={t(noDataTextKey)}
          disabled={!overflow}
          placement={tooltipPlacement}
        >
          <Text
            disabled
            isSecondary
            className={noDataTextClassName}
            qaName={QA_NAME_PROFILE_INFO_ITEM_VALUE}
            ref={setContentOverflowRef}
          >
            {t(noDataTextKey)}
          </Text>
        </FloatingTooltip>
      )}
    </EntityProfileHeaderInfoItemBase>
  )
}

export default EntityProfileHeaderInfoItemDeadline
